import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  LineString,
  MultiLineString,
  Point,
} from "geojson";
import { useEffect, useMemo, useState } from "react";
import {
  GeoJSON,
  MapContainer,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";

import {
  getFeatureMetadata,
  useAccessToken,
  useSelectedMarker,
  useSelectedOnlineNetworks,
  useSetSelectedMarker,
} from "../../../state/application-state-service";
import { getBucklingColor, getMarker, getStatuColor } from "../../../utils/utils";
import { createRoot } from "react-dom/client";
import { Box, Button, Typography } from "@mui/material";
import { getInputParamsFromApi, useAllTags, useBucklingStateSchema, useInputParams, useIsSearchActive, useNetwork, useParamFilterState, useResponseSchema, useSearchMap, useStoredLocationData, useStoredLocationParams, useStoredParameters } from "../../../state/services";
import { useInputParamState } from "../../../state/Input-param-state";
import { InputDialogForm, RecommendationPopup, TagDialogDetails } from "./HeatMap";
import { greyIcon, activeGreyIcon } from '../../../components/MarkerIcon';
import { AddCircleOutline, ExpandLess, ExpandMore, Recommend } from "@mui/icons-material";
import { hasActiveFilter } from "../../../utils/filterUtils";
import { useCoordPassSet } from "../../../hooks/useCoordPassSet";
import { DynamicInputParameters } from "../../../types/ApiData";
import { createTrackSectionFromGeoJson, GeoJsonNetworkAdapter } from "../../../utils/onlineMapsUtils";
import { useApplicationState } from "../../../state/application-state";
import ReusableDialog from "../../../components/Dialog";
import MarkerClusterGroup from "react-leaflet-cluster";


type NetworkProperties = {
  feature_id: string;
  element_type: string;
  name: string;
  tag: string;
  metadata_summary: unknown[];
  metadata: unknown;
};

type NetworkPointFeature = Feature<Point, NetworkProperties>;

type NetworkLineFeature = Feature<
  LineString | MultiLineString,
  NetworkProperties
>;

type NetworkFeatureCollection = FeatureCollection<
  Geometry,
  NetworkProperties
>;

const DEFAULT_COLOR = "#6E6E6E";

export const GeoJSONMap = ({
  children,
  showMarkers,
  setShowMarkers
}: {
  children: React.ReactNode;
  showMarkers: boolean;
  setShowMarkers: (item: boolean) => void;
}) => {
  const [selectedId, setSelectedId] = useState<any>({})
  const [formOpen, setFormOpen] = useState(false)
  const [popupOpen, setPopupOpen] = useState(false);
  const center: [number, number] = [50.820763302, 4.356058902];
  const [metadata, setMetadata] = useState<any>(null);
  const [tagsPopupOpen, setTagsPopupOpen] = useState(false)

  const geoJsonData = useSelectedOnlineNetworks();
  const selectedMarker = useSelectedMarker();
  const storedInputParams = useInputParams()
  const paramState = useInputParamState()
  const applicationState = useApplicationState()
  const accessToken = useAccessToken()
  const bucklingData = useStoredLocationData()
  const bucklingSchema = useBucklingStateSchema()
  const paramFilter = useParamFilterState()
  const savedGlobalParams = useStoredParameters() as DynamicInputParameters[]
  const storedLocationParams = useStoredLocationParams()
  const searchMap = useSearchMap()
  const isSearchActive = useIsSearchActive()
  const network = useNetwork()
  const section = useMemo(
    () =>
      createTrackSectionFromGeoJson(geoJsonData, {
        networkId: network.id ?? "",
        trackId: network.id ?? "",
        trackName: network.name || "Selected Track",
      }),
    [geoJsonData, network.id, network.name]
  );

  const transformedNetwork = useMemo<GeoJsonNetworkAdapter>(
    () => ({
      id: section?.networkId || network.id || "selected-online-network",
      edgesOfNetwork: section ? [section] : [],
    }),
    [section, network.id]
  );

  const { coordPassSet } = useCoordPassSet({
    network: transformedNetwork,
    savedGlobalParams,
    storedLocationParams,
    paramFilter,
    networkId: transformedNetwork.id,
  });

  const setSelectedMarker = useSetSelectedMarker();

  const getTrackColor = (coord: [number, number]) => {
    let bucklingStatus = { status: 0, color: '#6E6E6E' } // status 0:1:2 not calculated:buckling global:buckling individual
    let buckling_status: any = ''
    const key = `${coord[0]},${coord[1]}`
    if (typeof bucklingData[key] === "object" &&
      bucklingData[key] !== null &&
      Object.keys(bucklingData[key] as object).length > 0) {
      let val = bucklingData[key] as any
      bucklingStatus.status = 2
      buckling_status = val.buckling_status ?? 'g1'
      bucklingStatus.color = getBucklingColor(buckling_status, bucklingSchema, 'dark')
    }
    return bucklingStatus
  }

  const nodeData = useMemo<
    FeatureCollection<Point, NetworkProperties>
  >(() => {
    const features = geoJsonData.features.filter((feature: any): feature is NetworkPointFeature => {
      if (feature.geometry.type !== "Point") {
        return false;
      }

      const [longitude, latitude] = feature.geometry.coordinates;

      if (isSearchActive) {
        const edgeSearch = searchMap[network.id];
        if (!edgeSearch) return false;

        if (
          !edgeSearch.edgeMatch &&
          !edgeSearch.coordMatches.has(`${latitude}-${longitude}`)
        ) {
          return false;
        }
      }

      if (
        hasActiveFilter(paramFilter) &&
        coordPassSet &&
        !coordPassSet.has(`${latitude},${longitude}`)
      ) {
        return false;
      }

      return true;
    });

    return {
      type: "FeatureCollection",
      features,
    };
  }, [
    geoJsonData,
    network.id,
    isSearchActive,
    searchMap,
    paramFilter,
    coordPassSet,
  ]);

  const edgeData = useMemo<
    FeatureCollection<
      LineString | MultiLineString,
      NetworkProperties
    >
  >(
    () => ({
      type: "FeatureCollection",
      features: geoJsonData.features.filter(
        (feature: any): feature is NetworkLineFeature =>
          feature.geometry.type === "LineString" ||
          feature.geometry.type === "MultiLineString"
      ),
    }),
    [geoJsonData]
  );

  useEffect(() => {
    if(!showMarkers && selectedMarker) setShowMarkers(true)
  }, [selectedMarker])

  const pointToLayer = (
    feature: NetworkPointFeature,
    latlng: L.LatLng
  ): L.Layer => {
    const isSelected =
      feature.properties.feature_id === selectedMarker;

    let coord = [latlng.lat, latlng.lng] as [number, number]
    let statusInfo = { status: 'undefined', badgeColor: '#6E6E6E', textColor: 'white', state: '', icon: '' }
    let iconInfo = { icon: greyIcon, activeIcon: activeGreyIcon }
    let icon = iconInfo.icon

    if (typeof bucklingData[`${coord[0]},${coord[1]}`] === "object" &&
      bucklingData[`${coord[0]},${coord[1]}`] !== null &&
      Object.keys(bucklingData[`${coord[0]},${coord[1]}`] as object).length > 0) {
      let response = bucklingData[`${coord[0]},${coord[1]}`] as any
      statusInfo = getStatuColor(response.buckling_status, bucklingSchema)
    }
    iconInfo = getMarker(statusInfo.icon)
    icon = iconInfo.icon

    if (isSelected) {
      icon = iconInfo.activeIcon
    }

    if (isSelected) {
      return L.marker(latlng, {
        icon: icon,
        zIndexOffset: 1000,
        bubblingMouseEvents: false,
      });
    }

    return L.circleMarker(latlng, {
      radius: 6,
      color: statusInfo.badgeColor,
      fillColor: statusInfo.badgeColor,
      fillOpacity: 1,
      weight: 2,
      bubblingMouseEvents: false,
    });
  };

  const edgeStyle = (
    feature?: Feature<Geometry, NetworkProperties>
  ): L.PathOptions => {
    if (
      !feature ||
      feature.geometry.type !== "LineString"
    ) {
      return {
        color: DEFAULT_COLOR,
        weight: 4,
        opacity: 1,
        interactive: false,
      };
    }

    const [startLongitude, startLatitude] =
      feature.geometry.coordinates[0];

    const start: [number, number] = [
      startLatitude,
      startLongitude,
    ];

    const bucklingStatus = getTrackColor(start);

    return {
      color: bucklingStatus.color || DEFAULT_COLOR,
      weight: bucklingStatus.status === 2 ? 7 : 4,
      opacity: 1,
      interactive: false,
    };
  };

  const onEachNode = (
    feature: NetworkPointFeature,
    layer: L.Layer
  ) => {
    layer.on("click", (event: L.LeafletMouseEvent) => {
      L.DomEvent.stopPropagation(event.originalEvent);

      setSelectedMarker(feature.properties.feature_id);
    });
  };

  const onEachEdge = (
    _feature: NetworkLineFeature,
    layer: L.Layer
  ) => {
    if (layer instanceof L.Path) {
      layer.options.interactive = false;
    }
  };

  const displayForm = async (coord: any, connectedEdges: NetworkLineFeature[], feature: NetworkPointFeature) => {
    setSelectedId({ coord: coord, edgeName: network.name, edgeId: network.id, id: feature.properties.feature_id, name: feature.properties.name })
    if (storedInputParams.length === 0)
      await getInputParamsFromApi(paramState, accessToken)
    const result = await getFeatureMetadata(paramState,applicationState, feature.properties.feature_id, network.id, accessToken)
    setMetadata(result ?? null);
    setFormOpen(true)
  }

  const displayPopup = (coord: any) => {
    setSelectedId({ coord: coord })
    setPopupOpen(true)
  }

  const displayTagPopup = (coord: any) => {
    setSelectedId({ coord: coord })
    setTagsPopupOpen(true)
  }

  const closeTagPopup = () => {
    setSelectedId({})
    setTagsPopupOpen(false)
  }

  const closeForm = () => {
    setFormOpen(false)
    setSelectedId({})
    setMetadata(null)
  }

  const featureKey = geoJsonData.features
    .map((feature: any) => feature.properties?.feature_id ?? "")
    .join("-");

  const bucklingDataKey = JSON.stringify(bucklingData);
  const nodeDataKey = useMemo(
    () =>
      nodeData.features
        .map((feature: any) => feature.properties.feature_id)
        .sort()
        .join("|"),
    [nodeData]
  );
  const nodeKey = `nodes-${featureKey}-${selectedMarker ?? "none"}-${bucklingDataKey}-${nodeDataKey}`;
  const edgeKey = `edges-${featureKey}`;

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
      }}
    >
      <MapContainer
        center={center}
        zoom={18}
        style={{
          height: "100%",
          width: "100%",
        }}
        renderer={L.canvas()}
      >
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* Render non-interactive edges first. */}
        <GeoJSON
          key={edgeKey}
          data={edgeData}
          style={edgeStyle}
          onEachFeature={onEachEdge}
        />

        {/* Render nodes above edges. */}

          {showMarkers && <MarkerClusterGroup  spiderfyOnMaxZoom={false} chunkedLoading disableClusteringAtZoom={18}>
            <GeoJSON
              key={nodeKey}
              data={nodeData}
              pointToLayer={pointToLayer}
              onEachFeature={onEachNode}
            />
          </MarkerClusterGroup>}

        <FitToGeoJSON data={geoJsonData} selectedMarker={selectedMarker} />

        <SelectedNodePopup
          selectedMarker={selectedMarker}
          nodeData={nodeData}
          displayForm={displayForm}
          edgeData={edgeData}
          displayPopup={displayPopup}
          displayTagPopup={displayTagPopup}
          closeTagPopup={closeTagPopup}
          onPopupClose={() => setSelectedMarker(null)}
        />

        {children}
      </MapContainer>
      {(Object.keys(storedInputParams).length > 0 && formOpen) &&
        formOpen && <InputDialogForm metadata={metadata} storedInputParams={storedInputParams} selectedSection={selectedId} open={formOpen} closeForm={closeForm} />}
      {popupOpen && <RecommendationPopup commonResult={''} bucklingData={bucklingData} selectedSection={selectedId} open={popupOpen} setOpen={setPopupOpen} />}
      {tagsPopupOpen && <ReusableDialog open={tagsPopupOpen} cancelText='Close' title='Tag Details' content={<TagDialogDetails isGeoPoint={true} section={{coordinates:[{lat: selectedId.coord[0], long: selectedId.coord[1]}], railwayEdgeId: network.id, edgeName: network.name}}/>} onCancel={() => closeTagPopup()} />}     
    </div>
  );
};

type SelectedNodePopupProps = {
  selectedMarker: string | null;
  nodeData: FeatureCollection<Point, NetworkProperties>;
  edgeData: FeatureCollection<
    LineString | MultiLineString,
    NetworkProperties
  >;
  displayForm: (coord: any, connectedEdges: NetworkLineFeature[], feature: NetworkPointFeature) => void
  displayPopup: (coord: any) => void
  displayTagPopup: (coord: any) => void
  closeTagPopup: () => void
  onPopupClose: () => void
};

const SelectedNodePopup = ({
  selectedMarker,
  nodeData,
  displayForm,
  edgeData,
  displayPopup,
  displayTagPopup,
  closeTagPopup,
  onPopupClose
}: SelectedNodePopupProps) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedMarker) {
      map.closePopup();
      return;
    }

    const selectedFeature: any = nodeData.features.find(
      (feature) =>
        feature.properties.feature_id === selectedMarker
    );

    if (!selectedFeature) {
      map.closePopup();
      return;
    }

    const connectedEdges = edgeData.features.filter((edge) => {
      if (edge.geometry.type !== "LineString") {
        return false;
      }

      return edge.geometry.coordinates.some((coordinate: any) =>
        coordinatesEqual(
          selectedFeature.geometry.coordinates,
          coordinate as [number, number]
        )
      );
    });

    const [longitude, latitude] =
      selectedFeature.geometry.coordinates;

    const popupContainer = document.createElement("div");
    const root = createRoot(popupContainer);

    root.render(
      <NodePopupContent displayTagPopup={displayTagPopup} closeTagPopup={closeTagPopup} displayPopup={displayPopup} connectedEdges={connectedEdges} feature={selectedFeature} coord={[latitude, longitude]} displayForm={displayForm} />
    );

    const popup = L.popup({
      offset: [0,-20],
      maxWidth: 500,
      minWidth: 250,
      autoPan: true,
      closeButton: true,
    })
      .setLatLng([latitude, longitude])
      .setContent(popupContainer)
      .openOn(map);

    let isCleaningUp = false;

    const handlePopupRemove = () => {
      // Clear the selection only when Leaflet closes the popup,
      // not when this effect performs its cleanup.
      if (!isCleaningUp) {
        onPopupClose();
      }
    };
    popup.on("remove", handlePopupRemove);

    return () => {
      isCleaningUp = true;
      popup.off("remove", handlePopupRemove);
      if (map.hasLayer(popup)) {
        map.closePopup(popup);
      }
      root.unmount();
    };
  }, [selectedMarker, nodeData, map]);

  return null;
};

type NodePopupContentProps = {
  feature: NetworkPointFeature;
  displayForm: (coord: any, connectedEdges: NetworkLineFeature[], feature: NetworkPointFeature) => void
  coord: [number, number]
  connectedEdges: NetworkLineFeature[];
  displayPopup: (coord: any) => void
  displayTagPopup: (coord: any) => void
  closeTagPopup: () => void
};

const NodePopupContent = ({ feature, displayForm, coord, connectedEdges, displayPopup, displayTagPopup }: NodePopupContentProps) => {
  let bsm = ''
  let unit = ''
  let statusInfo = { status: 'undefined', badgeColor: '#6E6E6E', textColor: 'white', state: 'g1' }
  let recommend = false
  const [showTags, setShowTags] = useState(false);
  const bucklingSchema = useBucklingStateSchema()
  const responseSchema = useResponseSchema()
  const bucklingData = useStoredLocationData()
  const network = useNetwork()

  if (coord)
    if (typeof bucklingData[`${coord[0]},${coord[1]}`] === "object" &&
      bucklingData[`${coord[0]},${coord[1]}`] !== null &&
      Object.keys(bucklingData[`${coord[0]},${coord[1]}`] as object).length > 0) {
      let response = bucklingData[`${coord[0]},${coord[1]}`] as any
      bsm = response.bsm ?? ' ';
      statusInfo = getStatuColor(response.buckling_status, bucklingSchema)
      if (response?.recommendations && (response?.recommendations).length > 0) {
        recommend = true
      } else {
        recommend = false
      }
    }

  if (responseSchema && responseSchema.length > 0) {
    const item = responseSchema.find(item => item.code === 'bsm')
    unit = item?.unit ?? ''
  }
  const pointId = `${network.id}:${coord[0]}-${coord[1]}`

      const allTags = useAllTags()

      const pointTag = allTags[pointId];
      const edgeTag = allTags[`${network.id}:0-0`];
  
      const tag =
          pointTag || edgeTag
              ? {
                  ...(pointTag ?? edgeTag),
                  tags: Array.from(
                      new Set([
                          ...(edgeTag?.tags ?? []),
                          ...(pointTag?.tags ?? []),
                      ])
                  ),
              }
              : null;
  
      const tags = tag?.tags ?? [];
      const hasTags = tags.length > 0;

  return (
    <div className="flex flex-col gap-2 sm:gap-3 text-xs sm:text-sm max-w-[220px] sm:max-w-[320px]">
      <div>
        Selected Node:{" "}
        <span className="text-[#027034]">
          {feature.properties.name || "Node"}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <Button
          color="success"
          sx={{ textTransform: "none" }}
          variant="outlined"
          onClick={() => displayForm(coord, connectedEdges, feature)}
        >
          Calculate Track Stability
        </Button>

        {bsm !== '' && <div className='mt-3'>
          <span className="font-medium">Calculated BSM:</span>{" "}
          <span className="text-[#6f0689] font-semibold">{bsm} {unit}</span>
        </div>}
        {feature.properties.tag && <div className='mt-3'>
          <span className="font-medium">External Tags:</span>{" "}
          <span className="text-[#6f0689] font-semibold">{feature.properties.tag}</span>
        </div>}
      </div>

      {/* Tags Section */}
      <div className="border rounded-md p-2 bg-gray-50 mt-2 mb-1">
        {/* Header Row */}
        <div
          className="flex items-center justify-between cursor-pointer text-purple-700"
          onClick={() => setShowTags(prev => !prev)}
        >
          <span>
            {hasTags ? `Tags (${tags.length})` : "Tag Details"}
          </span>

          <span className="text-xs">
            {showTags ? <ExpandMore /> : <ExpandLess />}
          </span>
        </div>

        {/* Expanded */}
        {showTags && (
          <div className="mt-2 flex flex-wrap gap-1 max-h-[120px] overflow-y-auto">
            {hasTags ? (
              <>
                {tags.map((t: string, i: number) => (
                  <span
                    key={i}
                    className="bg-purple-100 text-[#027034] text-sm px-2 py-1 rounded-full truncate max-w-[220px]"
                    title={t}
                  >
                    {t}
                  </span>
                ))}

                <button
                  onClick={() => displayTagPopup(coord)}
                  className="text-purple-700 underline text-xs"
                >
                  <AddCircleOutline />
                </button>
              </>
            ) : (
              <button
                onClick={() => displayTagPopup(coord)}
                className="text-[#6f0689] underline text-xs"
              >
                Add Tags
              </button>
            )}

          </div>
        )}
      </div>

      {recommend && <><Button
        variant='contained'
        sx={{
          textTransform: "none",
          borderRadius: 2,
          padding: "8px",
          margin: '8px',
          fontWeight: "bold",
          color: "#8d42a8",
          backgroundColor: 'white',
          borderColor: "#8d42a8",
          "&:hover": {
            borderColor: "primary.dark",
            backgroundColor: "action.hover",
          },
          maxHeight: '40px'
        }}
        onClick={() => displayPopup(coord)}
        // className="p-0 text-left whitespace-nowrap"
        style={{ font: "inherit" }}
        endIcon={<Recommend sx={{
          animation: "pulse 1s ease-in-out infinite",
          "@keyframes pulse": {
            "0%": { transform: "scale(1)" },
            "50%": { transform: "scale(1.2)" }, // Grows
            "100%": { transform: "scale(1)" },
          },
        }} />}
      >
        <Typography noWrap sx={{ fontSize: "0.79rem" }}>Recommendations</Typography>
      </Button></>}

      {/* Status Badge */}
      <Box
        className="inline-block  px-3 py-1 text-xs font-bold rounded-md shadow"
        sx={{
          backgroundColor: statusInfo.badgeColor,
          color: statusInfo.textColor,
        }}
      >
        {statusInfo.status}
      </Box>
    </div>
  );
};


type FitToGeoJSONProps = {
  data: FeatureCollection<Geometry, GeoJsonProperties>;
  selectedMarker?: any;
};

const FitToGeoJSON = ({
  data,
  selectedMarker,
}: FitToGeoJSONProps) => {
  const map = useMap();

  useEffect(() => {
    if (!data.features.length) return;

    // Focus selected feature first
    if (selectedMarker) {
      const selectedFeature = data.features.find(
        (feature: any) =>
          feature.properties?.feature_id === selectedMarker
      );

      if (selectedFeature) {
        const selectedLayer = L.geoJSON(selectedFeature);
        const selectedBounds = selectedLayer.getBounds();

        if (selectedBounds.isValid()) {
          const northEast = selectedBounds.getNorthEast();
          const southWest = selectedBounds.getSouthWest();

          // Selected node
          if (northEast.equals(southWest)) {
            map.setView(selectedBounds.getCenter(), 19);
            return;
          }

          // Selected edge, if needed later
          map.fitBounds(selectedBounds, {
            padding: [50, 50],
            maxZoom: 19,
          });

          return;
        }
      }
    }

    // No selected marker -> fit whole network
    const geoJsonLayer = L.geoJSON(data);
    const bounds = geoJsonLayer.getBounds();

    if (!bounds.isValid()) return;

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    if (northEast.equals(southWest)) {
      map.setView(bounds.getCenter(), 18);
      return;
    }

    map.fitBounds(bounds, {
      padding: [40, 40],
      maxZoom: 18,
    });
  }, [data, selectedMarker, map]);

  return null;
};

const coordinatesEqual = (
  first: [number, number],
  second: [number, number],
  tolerance = 0.0000001
) =>
  Math.abs(first[0] - second[0]) < tolerance &&
  Math.abs(first[1] - second[1]) < tolerance;
